/**
 * Die Gliederung des Gewerks Fremdmodelle — an EINER Stelle.
 *
 * Muster und Anleitung für N2–N4 stehen ausführlich in
 * `seiten/heizlastkuehllast/abschnitte.ts` — hier nur der Unterschied, der
 * diesen Bereich zum SONDERFALL macht (Auftrag N1, Punkt 2: „einer beweist
 * nur den Sonderfall").
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM DIESE LISTE NUR EINEN EINTRAG HAT — UND WARUM DAS RICHTIG IST
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `Fremdmodelle.tsx` ist mit 68 Zeilen eine reine Verteilerseite (Plan
 * Abschnitt 3.7): Sie trägt selbst schon `LocalTabs` und einen `Outlet` für
 * ihre Kinder. Gemessen an den tatsächlichen Routen unter `fremdmodelle`:
 *
 *   fremdmodelle                    → Fremdmodellaufnahme (Index-Route)
 *   fremdmodelle/kueche             → Küchenplanung
 *   fremdmodelle/kueche/aufnahme    → Küchenaufnahme
 *   fremdmodelle/kueche/zuordnung   → Küchenzuordnung
 *   fremdmodelle/kueche/qualitaet   → Küchenqualität
 *
 * Der Plan nahm an, Küche hinge SCHON als Kind unter dem Fremdmodelle-Knoten
 * und Fremdmodellaufnahme/Modellvorgaben würden dort neue Geschwister. Beide
 * Annahmen halten der Messung an `navigation/struktur.ts` nicht stand:
 *
 * 1. **Küche ist bereits ein eigener Gewerks-WURZELKNOTEN** (`schluessel:
 *    'kueche'`, ohne `eltern`), mit eigener Begründung im Code: „Sie ist das
 *    einzige Modul, in dem die Datenbank fuehrt und das Modell das Ziel
 *    ist. Sie steht deshalb als eigenes Gewerk und nicht als Anhaengsel der
 *    Fremdmodelle." Das ist eine bestehende, bewusste Entscheidung — der
 *    Auftrag verlangt ausdrücklich „Küche bleibt, wo sie ist" (nicht
 *    umbauen). Sie NACHTRÄGLICH unter `fachbereich-fremdmodelle` zu hängen
 *    wäre der Umbau, den der Auftrag verbietet, und würde die eigene
 *    Fachlichkeit der Küche wieder verdecken.
 * 2. **`Fremdmodellaufnahme` braucht KEINEN eigenen Eintrag.** Sie ist die
 *    Index-Route von `fremdmodelle` (`<Route index element=
 *    {<Fremdmodellaufnahme />} />`) — ihr voller Pfad ist exakt
 *    `fremdmodelle`, identisch mit dem Pfad des Fachbereichsknotens selbst.
 *    Genau dafür existiert die Filterregel in `alleEintraege()` („Kind,
 *    dessen Pfad der seines Elternteils IST, entfällt"), am Beispiel von
 *    `elektro`/„Bauteile" bereits im Bestand. Ein zweiter Eintrag wäre eine
 *    zweite Route auf dieselbe Seite und stünde doppelt im Baum.
 * 3. **`Modellvorgaben.tsx` passt NICHT als Kind.** Ihre Sichtbarkeit ist
 *    `{ art: 'immer' }` und ihr Pfad absolut (`/modellvorgaben`) - sie gilt
 *    OHNE offenes Projekt (allgemeine Modellierungsregeln für Revit 2025,
 *    nicht projektgebunden). `fachbereich-fremdmodelle` dagegen existiert
 *    nur MIT Projekt (`sichtbarkeit: { art: 'fachbereich', ... }`). Als Kind
 *    eingehängt, verschwände „Modellvorgaben" entweder ganz (Elternknoten
 *    fehlt ohne Projekt, `alleEintraege()` sortiert das Kind aus) oder ihre
 *    eigene Immer-Sichtbarkeit müsste aufgegeben werden - beides eine
 *    Verschlechterung. Sie bleibt unverändert in VERWALTUNG stehen.
 *
 * **Ergebnis, gemessen:** Fremdmodelle hatte VOR N1 null Kinder in
 * `struktur.ts` und hat NACH N1 weiterhin null sichtbare Kinder — der
 * einzige Abschnittseintrag unten (`fremdmodelle` selbst, „Aufnahme") wird
 * von `alleEintraege()` automatisch herausgefiltert, weil sein Pfad dem
 * seines Elternknotens entspricht (Regel 2 oben). Er steht trotzdem hier,
 * aus demselben Grund wie `elektro`/„Bauteile": Vollständigkeit der
 * Reiterliste für diese Datei, nicht für den gerenderten Baum.
 *
 * Der Sonderfall, den dieser Bereich beweist: Das „einlesen, nicht
 * abschreiben"-Muster trägt auch dort, wo ein Bereich am Ende KEINEN neuen
 * sichtbaren Eintrag bekommt — weil die Route bereits die richtige Seite
 * zeigt und zwei naheliegende Ergänzungen aus eigenen, dokumentierten
 * Gründen NICHT eingehängt werden.
 */

export interface FremdmodelleEintrag {
  /** Der Pfad UNTER /projekte/:id/ — genau wie in App.tsx. */
  pfad: string;
  name: string;
  hinweis: string;
}

export interface FremdmodelleAbschnitt {
  name: string;
  eintraege: FremdmodelleEintrag[];
}

export const FREMDMODELLE_ABSCHNITTE: FremdmodelleAbschnitt[] = [
  {
    name: 'Aufnahme',
    eintraege: [
      {
        pfad: 'fremdmodelle',
        name: 'Fremdmodellaufnahme',
        hinweis: 'Was sich in Architektur, Tragwerk, Küche und '
          + 'Medientechnik seit dem Vorwochenstand geändert hat',
      },
    ],
  },
];
